import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import FirestoreService from '../assets/js/firestore_instance';
import { doc, getDoc } from 'firebase/firestore';
import { BsArrowLeft, BsCalendar, BsClock, BsGeoAlt, BsLink45Deg, BsPeople, BsQrCode } from 'react-icons/bs';
import { useFirebaseUsers } from '../hooks/useFirebaseUsers';
import QRCode from 'react-qr-code';


class MapErrorBoundary extends React.Component {
    constructor(props) {
      super(props);
      this.state = { hasError: false };
    }
  
    static getDerivedStateFromError(error) {
      return { hasError: true };
    }
  
    componentDidCatch(error, errorInfo) {
      console.error("Map error:", error, errorInfo);
    }
  
    render() {
      if (this.state.hasError) {
        return (
          <div className="h-60 rounded-lg overflow-hidden bg-gray-800 flex items-center justify-center">
            <div className="text-gray-400">
              <BsGeoAlt className="text-3xl mb-2 mx-auto" />
              <p>{this.props.address || 'Location information available'}</p>
            </div>
          </div>
        );
      }
      return this.props.children;
    }
  }

// Custom marker component for the map
const MapMarker = () => (
  <div className="text-red-500 text-4xl -mt-8 -ml-4">
    <BsGeoAlt />
  </div>
);

const GroupDetailPage = () => {
  const { groupTitle } = useParams();
  const [copyStatus, setCopyStatus] = useState({ show: false, success: false });
  const [showQR, setShowQR] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [group, setGroup] = useState(location.state?.groupData || null);
  const [isLoading, setIsLoading] = useState(!group);
  const [error, setError] = useState(null);
  const groupId = group.pco_id ? group.pco_id : group.jolt_group_id;
  
  // Extract leader and member IDs when group data is available
const leaderIds = useMemo(() => group?.leaders || [], [group]);
const memberIds = useMemo(() => group?.members?.filter(id => !leaderIds.includes(id)) || [], [group, leaderIds]);
  
  // Use the custom hook to fetch leaders and members
  const { 
    users: leaders, 
    isLoading: leadersLoading 
  } = useFirebaseUsers(leaderIds);
  
  const { 
    users: members, 
    isLoading: membersLoading 
  } = useFirebaseUsers(memberIds);

  useEffect(() => {
    const fetchGroupData = async () => {
      if (group) return; // Skip if we already have the group data from navigation state
      
      try {
        setIsLoading(true);
        const db = FirestoreService.getFirestore();
        const groupDoc = await getDoc(doc(db, "groups", groupId));
        
        if (groupDoc.exists()) {
          setGroup({...groupDoc.data(), id: groupId});
        } else {
          setError("Group not found");
        }
      } catch (err) {
        console.error("Error fetching group:", err);
        setError("Failed to load group data");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchGroupData();
  }, [groupId, group]);

  const handleGoBack = () => {
    // Navigate to dashboard with a state parameter indicating the groups tab should be active
    navigate('/dashboard', { state: { activeSection: 'groups' } });
  };

  // Check if we're still loading data
  if (isLoading || leadersLoading || membersLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <h1 className="text-2xl text-red-500 mb-4">Error</h1>
        <p className="text-gray-300 mb-6">{error || "Group not found"}</p>
        <button 
          onClick={handleGoBack}
          className="px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg text-white"
        >
          Go Back
        </button>
      </div>
    );
  }

  const mapProps = {
    center: {
      lat: parseFloat(group.latitude) || 0,
      lng: parseFloat(group.longitude) || 0
    },
    zoom: 15
  };

  const toggleQRCode = () => {
    setShowQR(!showQR);
  };

  return (
    <div className="min-h-screen min-w-screen p-8 dashboard-element-bg">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-6xl mx-auto"
      >
        <button 
          onClick={handleGoBack}
          className="flex items-center text-gray-300 hover:text-white mt-20"
        >
          <BsArrowLeft className="mr-2" /> Back to Groups
        </button>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-5 dashboard-main-color p-8 rounded-2xl shadow-lg">
          {/* Left column - Group image and basic info */}
          <div className="md:col-span-1">
            <div className="dashboard-on-element rounded-lg overflow-hidden shadow-lg">
              <div className="">
                <img 
                  src={group.grpAvatar} 
                  alt={group.title} 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h1 className="text-2xl font-bold mb-2 text-white">{group.title}</h1>
                <div className="text-gray-300 mb-4">
                  <div className="flex items-center mb-2">
                    <BsCalendar className="mr-2" />
                    <span>Meets {group.cadence || "Regularly"}</span>
                  </div>
                  {group.schedule && (
                    <div className="flex items-center mb-2 text-white">
                      <BsClock className="mr-2" />
                      <span>{group.schedule}</span>
                    </div>
                  )}
                  {group.address && (
                    <div className="flex items-center mb-2 text-white">
                      <BsGeoAlt className="mr-2" />
                      <span>{group.address}</span>
                    </div>
                  )}
                  {group.group_link && (
                    <div className="mb-4">
                        <div className="flex items-center mb-2">
                        <BsLink45Deg className="mr-2" />
                        <button 
                            onClick={() => {
                            navigator.clipboard.writeText(group.group_link)
                                .then(() => {
                                setCopyStatus({ show: true, success: true });
                                setTimeout(() => setCopyStatus({ show: false, success: true }), 2000);
                                })
                                .catch(err => {
                                console.error('Failed to copy: ', err);
                                setCopyStatus({ show: true, success: false });
                                setTimeout(() => setCopyStatus({ show: false, success: false }), 2000);
                                });
                            }}
                            className="text-orange-400 hover:text-orange-300 hover:underline flex items-center cursor-pointer"
                        >
                            Copy Group Link
                        </button>
                        <button 
                            onClick={toggleQRCode}
                            className="ml-2 p-1 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors"
                            title="Show QR Code"
                        >
                            <BsQrCode className="text-orange-400" />
                        </button>
                        </div>
                        
                        {/* Toast notification */}
                        {copyStatus.show && (
                        <div className={`mt-2 px-3 py-1 rounded-md inline-block text-sm ${
                            copyStatus.success ? 'bg-green-800 text-green-200' : 'bg-red-800 text-red-200'
                        }`}>
                            {copyStatus.success ? 'Link copied to clipboard!' : 'Failed to copy link'}
                        </div>
                        )}
                        
                        {showQR && (
                        <div className="mt-3 p-4 bg-gray-700 rounded-lg flex justify-center">
                            <QRCode 
                            value={group.group_link} 
                            size={150} 
                            bgColor="#374151" 
                            fgColor="#fff" 
                            level="Q" 
                            />
                        </div>
                        )}
                    </div>
                    )}
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-semibold mb-2 text-white">Enrollment</h3>
                  <span className={`px-3 py-1 text-sm rounded-full ${group.enrollment_open ? 'bg-green-700 text-green-100' : 'bg-red-900 text-red-100'}`}>
                    {group.enrollment_open ? 'Open' : 'Closed'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right column - Group details */}
          <div className="md:col-span-2">
            <div className="dashboard-on-element rounded-lg p-6 shadow-lg mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">About This Group</h2>
              <p className="text-gray-300 mb-4">{group.description || "No description available."}</p>
              
              {group.days && group.days.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-lg font-medium mb-2 text-white">Meeting Days</h3>
                  <div className="flex flex-wrap gap-2">
                    {group.days.map((day, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 bg-gray-600 rounded-full text-sm text-gray-300"
                      >
                        {day}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
            {(group.address || (group.latitude && group.longitude)) ? (
                <div className="mt-6">
                <h3 className="text-lg font-medium mb-2 text-white">Location</h3>
                <div className="h-80 rounded-lg overflow-hidden">
                    <iframe
                    title="Group Location"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyCBYehxlkQ2HkkIAnmlurba71wcJdTNC-0&q=${encodeURIComponent(group.address || `${group.latitude},${group.longitude}`)}&zoom=15`}
                    allowFullScreen
                    ></iframe>
                </div>
                <div className="mt-2 text-gray-300 flex items-center">
                    <BsGeoAlt className="mr-2 flex-shrink-0" />
                    <span>{group.address || 'Location information available'}</span>
                </div>
                </div>
            ) : null}


            </div>
            
            {/* People section */}
            <div className="dashboard-on-element rounded-lg p-6 shadow-lg">
              
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">People</h2>
              <div className="flex items-center text-gray-300">
                <BsPeople className="mr-2" />
                <span>{group.members ? group.members.length : 0} members</span>
              </div>
            </div>
              
              {/* Leaders */}
              {leaders.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium mb-3 text-gray-300">Leaders</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {leaders.map(leader => (
                      <div key={leader.id} className="flex items-center p-3 bg-gray-700 rounded-lg">
                        <div className="w-10 h-10 rounded-full overflow-hidden mr-3">
                          {leader.avatar ? (
                            <img src={leader.avatar} alt={leader.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-orange-700 flex items-center justify-center text-white font-medium">
                              {leader.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-gray-300">{leader.name}</div>
                          {leader.email && <div className="text-sm text-gray-300">{leader.email}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Members */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-medium text-gray-300">Members</h3>
                  
                </div>
                
                {members.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {members.map(member => (
                    <div key={member.id} className="flex items-center p-3 bg-gray-700 rounded-lg">
                      <div className="w-10 h-10 flex-shrink-0 rounded-full overflow-hidden mr-3">
                        {member.avatar ? (
                          <img src={member.avatar} alt={member.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gray-600 flex items-center justify-center text-white font-medium">
                            {member.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate text-gray-300">{member.name}</div>
                        {member.email && <div className="text-sm text-gray-300 truncate">{member.email}</div>}
                      </div>
                    </div>
                  ))}
                </div>
                ) : (
                  <p className="text-gray-400">No other members in this group.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default GroupDetailPage;